// AI news feed — content and pure helpers, kept separate from presentation.

const HN_TOP_STORIES_URL =
  "https://hacker-news.firebaseio.com/v0/topstories.json";
const hnItemUrl = (id) => `https://hacker-news.firebaseio.com/v0/item/${id}.json`;

// Only headlines matching this pattern are kept.
const AI_KEYWORDS =
  /ai|gpt|openai|claude|anthropic|llm|grok|xai|codex|agent|transformer|diffusion|machine learning|deep learning|language model|vibe coding|cursor|copilot/i;

const FETCH_BATCH_SIZE = 45;
const VISIBLE_ITEMS = 6;

// Shown while the live feed is unreachable.
const fallbackNews = [
  {
    title: "OpenAI 模型与 API 状态面板",
    url: "https://status.openai.com/",
    host: "status.openai.com",
    by: "status.openai.com",
  },
  {
    title: "ChatGPT — OpenAI 官方入口",
    url: "https://chatgpt.com/",
    host: "chatgpt.com",
    by: "openai",
  },
  {
    title: "Grok — xAI 官方入口",
    url: "https://grok.com/",
    host: "grok.com",
    by: "xai",
  },
  {
    title: "Claude — Anthropic 官方入口",
    url: "https://claude.ai/",
    host: "claude.ai",
    by: "anthropic",
  },
  {
    title: "Codex / Claude Code 使用教程（飞书文档）",
    url: "https://pcnua8dpmgop.feishu.cn/docx/SSYRdjDQyouztxxJfWcc5ZV3n7f",
    host: "feishu.cn",
    by: "15Token",
  },
];

function formatRelativeTime(ts) {
  if (!ts) return "just now";
  const diff = Math.max(0, Date.now() / 1000 - ts);
  if (diff < 60) return "刚刚";
  if (diff < 3600) return `${Math.floor(diff / 60)} 分钟前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} 小时前`;
  return `${Math.floor(diff / 86400)} 天前`;
}

function hostOf(url) {
  try {
    return url
      ? new URL(url).hostname.replace(/^www\./, "")
      : "news.ycombinator.com";
  } catch {
    return "link";
  }
}

// Normalize one Hacker News item into the shape the view renders.
function toNewsItem(item) {
  const url = item.url || `https://news.ycombinator.com/item?id=${item.id}`;
  return {
    id: item.id,
    title: item.title,
    url,
    host: hostOf(item.url),
    by: item.by || "source",
    score: item.score,
    time: item.time,
  };
}

async function fetchTopStoryIds() {
  const response = await fetch(HN_TOP_STORIES_URL, { cache: "no-store" });
  if (!response.ok) throw new Error("topstories request failed");
  return response.json();
}

async function fetchItems(ids) {
  const results = await Promise.all(
    ids.slice(0, FETCH_BATCH_SIZE).map(async (id) => {
      try {
        const response = await fetch(hnItemUrl(id), { cache: "no-store" });
        return response.ok ? await response.json() : null;
      } catch {
        return null;
      }
    }),
  );
  return results;
}

// Fetch, filter by AI keywords, rank by score, and fall back when empty.
async function loadAiNews() {
  try {
    const ids = await fetchTopStoryIds();
    const items = await fetchItems(ids);
    const ranked = items
      .filter((item) => item && item.title && AI_KEYWORDS.test(item.title))
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, VISIBLE_ITEMS)
      .map(toNewsItem);
    return ranked.length
      ? { items: ranked, offline: false }
      : { items: fallbackNews, offline: true };
  } catch {
    return { items: fallbackNews, offline: true };
  }
}

export {
  AI_KEYWORDS,
  VISIBLE_ITEMS,
  fallbackNews,
  formatRelativeTime,
  hostOf,
  loadAiNews,
};
